import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { departmentApi } from '../../api/department.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Spinner } from '../../components/ui/index';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

/** Dispatcher-only: grant each department's manager the ability to define
 * new companies/users (addCompanyWithNewUser/addCompanyWithExistUser), and
 * optionally let that manager delegate the same ability to employees in
 * their own department (single level - see ManageCompanyDelegationPage). */
export default function ManagerCompanyPermissionsPage() {
  const { addToast } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [managers, setManagers] = useState([]);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await departmentApi.getDepartmentManagersPermissions();
      setManagers(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (showSpinner) addToast('خطا در بارگذاری فهرست مدیران دپارتمان‌ها', 'error');
    }
    setLoading(false);
  }, [addToast]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: !!saving });

  const toggle = async (manager, field) => {
    const next = { ...manager, [field]: !manager[field] };
    // Revoking the ability to define company/user should also revoke the
    // right to delegate it - a manager can't hand out a permission they no
    // longer hold themselves.
    if (field === 'can_manage_companies' && !next.can_manage_companies) {
      next.can_delegate_manage_companies = false;
    }
    setSaving(manager.employee_id);
    try {
      await departmentApi.setManagerCompanyPermission({
        employee_id: manager.employee_id,
        can_manage_companies: next.can_manage_companies ? 1 : 0,
        can_delegate_manage_companies: next.can_delegate_manage_companies ? 1 : 0,
      });
      setManagers((prev) => prev.map((m) => (m.employee_id === manager.employee_id ? next : m)));
    } catch {
      addToast('خطا در ذخیره دسترسی', 'error');
    }
    setSaving(null);
  };

  return (
    <div className="fade-in">
      <PageHeader
        title="دسترسی تعریف شرکت و کاربر"
        subtitle="مشخص کنید مدیر کدام دپارتمان‌ها اجازه تعریف شرکت و کاربر جدید را دارد، و کدام‌یک می‌تواند این اجازه را به کارمندان دپارتمان خود بدهد"
        action={<Link to="/settings" style={{ fontSize: 13, color: '#64748b' }}>بازگشت به تنظیمات</Link>}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spinner /></div>
      ) : !managers.length ? (
        <div style={{ fontSize: 13, color: '#94a3b8' }}>هیچ دپارتمانی با مدیر مشخص یافت نشد.</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', overflow: 'hidden', maxWidth: 760 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                <th style={{ textAlign: 'right', padding: '10px 16px', color: '#64748b', fontWeight: 600 }}>دپارتمان</th>
                <th style={{ textAlign: 'right', padding: '10px 16px', color: '#64748b', fontWeight: 600 }}>مدیر</th>
                <th style={{ padding: '10px 16px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>تعریف شرکت/کاربر</th>
                <th style={{ padding: '10px 16px', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>واگذاری به کارمندان</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((m) => (
                <tr key={m.employee_id} style={{ borderTop: '1px solid #f1f5f9', opacity: saving === m.employee_id ? 0.5 : 1 }}>
                  <td style={{ padding: '10px 16px', fontWeight: 600, color: '#0f172a' }}>{m.department_name}</td>
                  <td style={{ padding: '10px 16px', color: '#334155' }}>{`${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}</td>
                  <td style={{ textAlign: 'center', padding: '10px 16px' }}>
                    <input
                      type="checkbox"
                      disabled={saving === m.employee_id}
                      checked={m.can_manage_companies}
                      onChange={() => toggle(m, 'can_manage_companies')}
                    />
                  </td>
                  <td style={{ textAlign: 'center', padding: '10px 16px' }}>
                    <input
                      type="checkbox"
                      disabled={saving === m.employee_id || !m.can_manage_companies}
                      checked={m.can_delegate_manage_companies}
                      onChange={() => toggle(m, 'can_delegate_manage_companies')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
